import React from 'react';

/**
 * EmptyState — replaces ad-hoc emoji-based empty cells (audit #32).
 *
 * Use anywhere a list, dashboard, or panel renders "nothing here yet".
 * Material Symbol icon (stone-400) preferred over emojis like 📭 — emoji
 * rendering is inconsistent across Windows / macOS / Android and dilutes
 * brand identity.
 */

export interface EmptyStateProps {
  /** Material Symbol Rounded name, e.g. "mail_outline", "inbox", "info" */
  icon: string;
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** Compact variant uses less vertical space */
  compact?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, description, action, compact = false }) => (
  <div
    className={[
      'bg-stone-50 border border-stone-200 rounded-2xl text-center',
      compact ? 'p-5' : 'p-8',
    ].join(' ')}
  >
    <span
      className="material-symbols-rounded text-stone-400"
      style={{ fontSize: compact ? 32 : 40 }}
      aria-hidden="true"
    >
      {icon}
    </span>
    <div className={`${compact ? 'mt-2' : 'mt-3'} text-base font-medium text-stone-700`}>{title}</div>
    {description && (
      <div className="text-sm text-stone-500 mt-1 leading-relaxed max-w-md mx-auto">
        {description}
      </div>
    )}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export default EmptyState;
